import React, { useEffect, useRef, useState } from "react";
import api from "../../helpers/Api";

export default function QueueWaiting({ queueData, eventId, onActive }) {
    const [position, setPosition] = useState(queueData?.position ?? null);
    const [estimatedMinutes, setEstimatedMinutes] = useState(queueData?.estimated_minutes ?? null);
    const [expired, setExpired] = useState(false);
    const intervalRef = useRef(null);

    function getPollingInterval(pos) {
        if (pos === null) return 10000;
        if (pos <= 50) return 4000;
        if (pos <= 500) return 15000;
        return 45000;
    }

    async function checkStatus() {
        try {
            const res = await api.get(`/Queue/Status?token=${queueData.token}&event_id=${eventId}`);
            const data = res?.data;

            if (data?.status === "active") {
                clearInterval(intervalRef.current);
                const stored = JSON.parse(localStorage.getItem(`queue_token_${eventId}`) || "{}");
                stored.status = "active";
                stored.expires_at = data.expires_at;
                localStorage.setItem(`queue_token_${eventId}`, JSON.stringify(stored));
                onActive(data.expires_at);
                return;
            }

            if (data?.status === "expired") {
                clearInterval(intervalRef.current);
                localStorage.removeItem(`queue_token_${eventId}`);
                setExpired(true);
                return;
            }

            if (data?.position !== undefined) {
                setPosition(data.position);
                setEstimatedMinutes(data.estimated_minutes);

                clearInterval(intervalRef.current);
                intervalRef.current = setInterval(checkStatus, getPollingInterval(data.position));
            }
        } catch {
        }
    }

    useEffect(() => {
        intervalRef.current = setInterval(checkStatus, getPollingInterval(position));
        return () => clearInterval(intervalRef.current);
    }, []);

    function handleLeave() {
        clearInterval(intervalRef.current);
        api.post("/Queue/Leave", { token: queueData.token, event_id: eventId }).catch(() => {});
        localStorage.removeItem(`queue_token_${eventId}`);
        window.history.back();
    }

    function handleRetry() {
        window.location.reload();
    }

    if (expired) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <h2 style={styles.title}>Tu lugar expiró</h2>
                    <p style={styles.subtitle}>Tu tiempo en la fila terminó. Puedes volver a intentarlo.</p>
                    <button style={styles.btnPrimary} onClick={handleRetry}>Reintentar</button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h2 style={styles.title}>Estás en la fila</h2>
                {position !== null && (
                    <div style={styles.positionBox}>
                        <span style={styles.positionNumber}>{position}</span>
                        <span style={styles.positionLabel}>tu posición</span>
                    </div>
                )}
                {estimatedMinutes !== null && (
                    <p style={styles.subtitle}>
                        Tiempo estimado de espera: <strong>{estimatedMinutes} min</strong>
                    </p>
                )}
                <p style={styles.hint}>Te avisaremos automáticamente cuando sea tu turno.</p>
                <button style={styles.btnSecondary} onClick={handleLeave}>Salir de la fila</button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
        padding: "2rem",
    },
    card: {
        background: "#fff",
        borderRadius: "1rem",
        padding: "2.5rem 2rem",
        maxWidth: "420px",
        width: "100%",
        textAlign: "center",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    },
    title: {
        fontSize: "1.5rem",
        fontWeight: 700,
        marginBottom: "1rem",
        color: "#1a1a2e",
    },
    positionBox: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "1rem 0",
    },
    positionNumber: {
        fontSize: "4rem",
        fontWeight: 800,
        color: "#e63946",
        lineHeight: 1,
    },
    positionLabel: {
        fontSize: "0.85rem",
        color: "#666",
        marginTop: "0.25rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
    },
    subtitle: {
        color: "#444",
        marginBottom: "0.75rem",
    },
    hint: {
        fontSize: "0.85rem",
        color: "#888",
        marginBottom: "1.5rem",
    },
    btnPrimary: {
        background: "#e63946",
        color: "#fff",
        border: "none",
        borderRadius: "0.5rem",
        padding: "0.75rem 2rem",
        fontSize: "1rem",
        cursor: "pointer",
        fontWeight: 600,
    },
    btnSecondary: {
        background: "transparent",
        color: "#888",
        border: "1px solid #ddd",
        borderRadius: "0.5rem",
        padding: "0.6rem 1.5rem",
        fontSize: "0.9rem",
        cursor: "pointer",
    },
};
